import React from 'react';
// template components
import Accordion from '@nmx/template/dist/componentLibrary/modules/Accordion';
import BulletedList from '@nmx/template/dist/componentLibrary/components/BulletedList';
import Col from '@nmx/template/dist/componentLibrary/foundations/Grid/Col';
import Link from '@nmx/template/dist/componentLibrary/components/Link';
import Row from '@nmx/template/dist/componentLibrary/foundations/Grid/Row';
import SectionContainer from '@nmx/template/dist/componentLibrary/components/SectionContainer';
import Typography from '@nmx/template/dist/componentLibrary/foundations/Typography';
import WidowBlock from '@nmx/template/dist/componentLibrary/components/WidowBlock';
// icons
import MinusSignFillIcon from '@nmx/template/dist/componentLibrary/foundations/Icon/icons/utility/MinusSignFill';
import PlusSignFillIcon from '@nmx/template/dist/componentLibrary/foundations/Icon/icons/utility/PlusSignFill';

export default () => (
  <SectionContainer
    id='brokerage-accounts-services-faq' // '-section-container' will be appended dynamically
    className='nmx-no-print'
    backgroundVariant='lightA'
    themeType='lightTheme'
    moduleName='faq-module'
    moduleVariation='A'>
    <Row align='center'>
      <Col
        align='center'
        small={10}
        medium={8}
        large={6}>
        <Typography
          component='h2'
          nextElement='typography'>
          Questions about brokerage accounts and services&#63; <WidowBlock>We&#39;ve got answers.</WidowBlock>
        </Typography>
      </Col>
    </Row>
    <Row align='center'>
      <Col
        medium={10}
        large={8}>
        <Accordion
          id='faq-brokerage-accounts-services'
          variant='secondary'
          toggleOpenIcon={PlusSignFillIcon}
          toggleCloseIcon={MinusSignFillIcon}
          isFAQ
          items={[
            {
              id: 'brokerage-accounts-services-how-do-brokerage-accounts-work-faq',
              title: <>How does a brokerage <WidowBlock>account work&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    A brokerage account is an investment account you open with a brokerage firm. Once you open and fund your account, you can place orders to buy and sell many&nbsp;
                    <Link
                      id='brokerage-accounts-types-of-investments'
                      linkUrl='/life-and-money/types-of-investments/'
                      variant='primaryDeemphasize'
                      ariaLabel='Learn more about different types of investments'>
                      types of investments
                    </Link>, including stocks, bonds, mutual funds, and Exchange Traded Funds &#40;ETFs&#41;.
                  </Typography>
                  <Typography disableBottomPadding>
                    Ready to open your account at Northwestern Mutual&#63;&nbsp;
                    <Link
                      id='brokerage-accounts-services-open-your-account-with-a-financial-advisor'
                      linkUrl='/find-a-financial-advisor/'
                      variant='primaryDeemphasize'
                      ariaLabel='Learn more about how a brokerage service account works'>
                      Get matched with a financial advisor
                    </Link>.
                  </Typography>
                </>
              ),
            },
            {
              id: 'brokerage-accounts-services-brokerage-account-differences-faq',
              title: <>How are brokerage accounts different from <WidowBlock>bank accounts&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    Our brokerage accounts have features that are similar to a bank account&#8212;you can deposit and withdraw money when you wish, use checks, debit cards, and make automatic payments. However, there are a couple major differences between them.
                  </Typography>
                  <Typography disableBottomPadding>
                    Your brokerage account gives you the ability to buy and sell securities &#40;e.g., mutual funds, ETFs, stocks, bonds, etc.&#41; to help grow your money, while a bank account typically helps you manage your cash through a savings or checking account. Brokerage accounts are also not FDIC Insured, while bank balances are &#40;up to &#36;250K per depositor&#41;. The insurance you get for a brokerage account only protects you against brokerage failure &#40;SIPC&#41;.&nbsp;
                    <Link
                      id='brokerage-accounts-services-difference-between-banks-credit-unions-and-brokerages'
                      linkUrl='/life-and-money/whats-the-difference-between-banks-credit-unions-and-brokerages/'
                      variant='primaryDeemphasize'
                      ariaLabel='Learn more about the differences between brokerage services and bank accounts'>
                      Learn more about the differences
                    </Link>.
                  </Typography>
                </>
              ),
            },
            {
              id: 'brokerage-accounts-services-advisor-vs-agent-faq',
              title: <>What&#39;s the difference between an investment advisor and an <WidowBlock>insurance agent&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    An investment advisor, also known as a financial advisor, has gone through special training to earn credentials indicating that they have the expertise to offer investment advice and products to you. Our financial advisors will put your interests first. This means that any recommendations they make to you will be tailored to your specific needs, preferences, and financial circumstances.
                  </Typography>
                  <Typography disableBottomPadding>
                    An insurance agent is trained and licensed to offer you the right type of policies and coverage amounts to suit your needs and financial situation. Insurance agents also stay in touch after the policy is purchased, checking in periodically to make sure that it&#39;s still the best fit for you. Since we consider life insurance the foundation for most financial plans, your insurance agent should be an important part of your financial planning team.&nbsp;
                    <Link
                      id='brokerage-accounts-services-life-insurance-agent-broker-vs-financial-advisor-link'
                      linkUrl='/life-and-money/life-insurance-agent-broker-vs-financial-advisor/'
                      variant='primaryDeemphasize'
                      ariaLabel='Learn more about the differences between an investment advisor and an insurance agent'>
                      Find out more
                    </Link>.
                  </Typography>
                </>
              ),
            },
            {
              id: 'brokerage-accounts-services-role-of-financial-advisors-faq',
              title: <>What is the role of financial advisors in <WidowBlock>brokerage services&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <Typography disableBottomPadding>
                  Our advisors start by looking at your financial big picture, giving you expert guidance and recommending investments to help you reach your goals. They&#39;ll help you access investments that can align to your goals, but they do not offer ongoing monitoring of your investments. You&#39;ll have full control over your account and make all decisions about when to purchase, hold, or sell any of your investments. Ready to start investing&#63;&nbsp;
                  <Link
                    id='brokerage-accounts-services-start-investing-with-a-financial-advisor-link'
                    linkUrl='/find-a-financial-advisor/'
                    variant='primaryDeemphasize'
                    ariaLabel='Get matched with a financial advisor for more guidance on investments'>
                    Get matched with a financial advisor
                  </Link>.
                </Typography>
              ),
            },
            {
              id: 'brokerage-accounts-services-regulated-faq',
              title: <>Are brokerage <WidowBlock>services regulated&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography  disableBottomPadding>
                    Yes, they are. Northwestern Mutual is a member of the Financial Industry Regulatory Authority &#40;FINRA&#41; and registered with the Securities and Exchange Commission &#40;SEC&#41;. Ready to get started&#63;&nbsp;
                    <Link
                      id='brokerage-accounts-talk-to-an-advisor-about-regulated-brokerage-services-link'
                      linkUrl='/find-a-financial-advisor/'
                      variant='primaryDeemphasize'
                      ariaLabel='Get matched with a financial advisor to learn more about brokerage services'>
                      Get matched with a financial advisor
                    </Link>.
                  </Typography>
                </>
              ),
            },
            {
              id: 'brokerage-accounts-services-can-my-investments-go-down-faq',
              title: <>Can the value of my brokerage account <WidowBlock>go down&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <Typography disableBottomPadding>
                  All investments carry some level of risk, including the potential loss of principal up to the full value of your investment, and no investment strategy can guarantee gains or protect against loss. Want to know more&#63;&nbsp;
                  <Link
                    id='brokerage-accounts-risks-of-invesments-link'
                    linkUrl='/life-and-money/common-investing-risks-and-how-to-manage-them/'
                    ariaLabel='Learn more about the types of investing risks in a brokerage account'
                    variant='primaryDeemphasize'>
                    Find out the types of investing risks
                  </Link>.
                </Typography>
              ),
            },
            {
              id: 'brokerage-accounts-services-how-to-diversify-faq',
              title: <>How can I diversify my <WidowBlock>brokerage account&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    There are several diversification strategies you can use to help manage risk in your portfolio.
                  </Typography>
                  <BulletedList bulletType='filledCircle'
                    bulletedListData={[
                      { bulletText: <><strong>Asset classes</strong>: Investing in different stocks, bonds, real estate, or commodities can spread your risk across various types of investments.</> },
                      { bulletText: <><strong>Geographic</strong>: Investing in different regions or countries can help mitigate the risk associated with a single market.</> },
                      { bulletText: <><strong>Sector</strong>: Investing in different sectors or industries like technology, healthcare, finance, or energy can help reduce the impact of a downturn in a particular sector.</> },
                      { bulletText: <><strong>Company</strong>: Investing companies of different sizes across industries can reduce the risk associated with individual stocks.</> },
                    ]}
                  />
                  <Typography disableBottomPadding>
                    Want to know more&#63;&nbsp;
                    <Link
                      id='brokerage-accounts-guide-to-building-a-diverse-portfolio-link'
                      linkUrl='/life-and-money/the-best-way-to-invest-money/'
                      ariaLabel='Get to know more by reading our guide on building a diversified portfolio'
                      variant='primaryDeemphasize'>
                      Get our guide on building a diversified portfolio
                    </Link>.
                  </Typography>
                </>
              ),
            },
            {
              id: 'brokerage-accounts-services-do-i-have-to-pay-taxes-faq',
              title: <>Do I have to pay taxes on my <WidowBlock>brokerage account&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    There are several types of brokerage accounts, each with different tax treatment. A standard brokerage account is generally taxable, and you&#39;ll need to pay taxes on any capital gains, dividends, or interest. Short&#45; and long&#45;term capital gains as well as qualified and unqualified dividends are all taxed differently, and your tax rate will depend on your income level and filing status. When it comes to retirement and education savings accounts &#40;IRAs and 529 plans&#41;, those are tax&#45;deferred, which means you are not taxed on earnings in your account, but you could owe taxes when you withdraw the money.
                  </Typography>
                  <Typography disableBottomPadding>
                    <Link
                      id='brokerage-accounts-talk-to-an-advisor-about-different-types-of-brokerage-accounts'
                      linkUrl='/find-a-financial-advisor/'
                      variant='primaryDeemphasize'
                      ariaLabel='Learn more about the different types of brokerage accounts and tax treatments'>
                      Talk to an advisor
                    </Link>
                    &nbsp;about the types of investments that are best for your tax situation and financial goals.
                  </Typography>
                </>
              ),
            },
            {
              id: 'brokerage-accounts-services-online-brokerage-accounts-services-faq',
              title: <>Are online brokerage <WidowBlock>services available&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    First, you&#39;ll need to meet with a financial advisor to get your brokerage account set up. Then your advisor will design an investment strategy tailored to your goals. Once your account is set up, you can go online to:
                  </Typography>
                  <BulletedList
                    bulletType='filledCircle'
                    bulletedListData={[
                      { bulletText: 'Review detailed information about your account' },
                      { bulletText: 'Monitor your account balance' },
                      { bulletText: <>Make one&#45;time deposits to your brokerage account or contribute to your IRA</> },
                      { bulletText: <>Sign up for e&#45;Delivery</> },
                      { bulletText: 'Connect your other accounts to see your financial picture all in one place' },
                    ]}
                  />
                  <Typography disableBottomPadding>
                    Ready to open a brokerage account&#63;&nbsp;
                    <Link
                      id='open-a-brokerage-account-with-a-financial-advisor'
                      linkUrl='/find-a-financial-advisor/'
                      variant='primaryDeemphasize'
                      ariaLabel='Connect with a financial advisor to get access to online investment services'>
                      Connect with an advisor to get started
                    </Link>.
                  </Typography>
                </>
              ),
            },
            {
              id: 'brokerage-accounts-services-how-can-i-open-faq',
              title: <>How can I open a <WidowBlock>brokerage account&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    <Link
                      id='brokerage-account-services-how-to-start-investing-link'
                      linkUrl='/life-and-money/how-to-start-investing/'
                      variant='primaryDeemphasize'>
                      Getting started with investing
                    </Link>
                    &nbsp;is easy. All it takes is a conversation with one of our financial advisors. We understand that talking finances might not come naturally for you, so you&#39;ll be happy to know that our advisors are expert listeners who keep the conversation free of any jargon or judgments. We&#39;ll look at all aspects of your financial life, then create tailored investment strategies within your financial plan designed to help you reach your goals now, and down the road.
                  </Typography>
                  <Typography disableBottomPadding>
                    Sound good&#63;&nbsp;
                    <Link
                      id='brokerage-account-services-start-investing-with-an-advisor-link'
                      linkUrl='/find-a-financial-advisor/'
                      variant='primaryDeemphasize'
                      ariaLabel='Connect with a financial advisor to get started on investing'>
                      Start investing with us by getting matched with an advisor
                    </Link>.
                  </Typography>
                </>
              ),
            },
            {
              id: 'brokerage-accounts-services-minimum-needed-faq',
              title: <>What is the minimum amount needed to open a <WidowBlock>brokerage account&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <Typography disableBottomPadding>
                  We do not require investment minimums to open a brokerage account with Northwestern Mutual, but some individual products may require investment minimums. Just starting out&#63; Read our guide to&nbsp;
                  <Link
                    id='brokerage-account-services-investing-for-beginners-link'
                    linkUrl='/life-and-money/how-to-start-investing/'
                    ariaLabel='Read our guide to investing for beginners to learn more about brokerage account services'
                    variant='primaryDeemphasize'>
                    investing for beginners
                  </Link>. Of course, you can always&nbsp;
                  <Link
                    id='brokerage-account-services-learn-more-get-matched-link'
                    linkUrl='/find-a-financial-advisor/'
                    ariaLabel='Get matched with a financial advisor to learn more about brokerage accounts'
                    variant='primaryDeemphasize'>
                    get matched with a financial advisor now
                  </Link>.
                </Typography>
              ),
            },
          ]}
        />
      </Col>
    </Row>
  </SectionContainer>
);
